import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, FormInput } from 'components/common';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { FaUserTimes, FaRegSave, FaBan } from 'react-icons/fa';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { adjustToNoon } from 'helpers/utils';
import { createRequestTerminalAccount } from 'redux/request/terminalAccountSlice';
import { getDepartmentBySite, getActiveSites } from 'redux/organization/siteSlice';
const RequestTerminalAccountForm = () => {
  const { data: user } = useSelector((state) => state.profile_user);
  const { activeData: siteData } = useSelector((state) => state.organization_site);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: {
      site_id: user?.site?.id
    },
    mode: 'onChange',
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedSite, setSelectedSite] = useState(user?.site?.id);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    setValue('terminal_date', adjustToNoon(new Date()));
    setValue('site_id', user?.site?.id);

    if (user?.department?.id) {
      setValue('department_id', user.department.id);
    }
  }, [setValue, user]);

  const siteOptions = useMemo(
    () => siteData && siteData.map((site) => ({ value: site?.id, label: site?.name })),
    [siteData]
  );

  const defaultSiteOption = useMemo(
    () => ({ value: user?.site?.id, label: user?.site?.name }),
    [user]
  );

  useEffect(() => {
    if (!siteData || siteData.length === 0) {
      dispatch(getActiveSites());
    }
  }, [dispatch, siteData]);

  useEffect(() => {
    const fetchDepartments = async (siteId) => {
      if (!siteId) return;

      setLoadingDepartments(true);
      try {
        const response = await dispatch(getDepartmentBySite(siteId)).unwrap();

        const options = response.rows
          .map((item) => item.attributes)
          .map((item) => ({ value: item.id, label: item.name }));

        setDepartmentOptions(options);
      } catch (error) {
        toast.error(error.message);
        setDepartmentOptions([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    if (selectedSite) {
      fetchDepartments(selectedSite);
    }
  }, [dispatch, selectedSite]);

  const handleSiteChange = (selectedOption) => {
    const newSiteId = selectedOption?.value;
    setSelectedSite(newSiteId);
    setValue('site_id', newSiteId, { shouldValidate: true });
    setValue('department_id', null);
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const navigateConfirmationRoute = (id) => {
    navigate(`/request/terminal-accounts/${id}/confirmation`);
  };

  const onSubmit = async (data) => {
    try {
      const response = await dispatch(createRequestTerminalAccount(data)).unwrap();
      const payload = response.data;

      if (payload && payload.id !== undefined) {
        navigateConfirmationRoute(payload.id);
      }
    } catch (error) {
      const msgs = error?.error;
      const message = Array.isArray(msgs) ? msgs.join('\n') : (error?.message || 'An error occurred.');
      toast.error(message, { style: { whiteSpace: 'pre-line' } });
    }
  };

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-100 tw-text-sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Card Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-slate-100">
          <h5 className="tw-text-base tw-font-semibold tw-text-slate-800 tw-flex tw-items-center tw-gap-2 tw-m-0">
            <FaUserTimes className="tw-text-red-500" />
            New Request Terminal Account
          </h5>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer"
            aria-label="Go back"
          >
            <span className="tw-text-xs">&#8592;</span>
          </button>
        </div>

        {/* Card Body */}
        <div className="tw-px-5 tw-py-5 tw-space-y-4">
          <Alert
            type="warning"
            message="This form lets you provide the information IT needs to terminate Portal user accounts. Fill in all required details and submit to proceed."
          />

          {/* Name */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Name<span className="tw-text-red-500 tw-ml-0.5">*</span>
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <FormInput
                type="text"
                placeholder="Full of name or 3 characters as policy"
                error={errors.name?.message}
                {...register('name', { required: 'Name must be required.' })}
              />
            </div>
          </div>

          {/* Email */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Email<span className="tw-text-red-500 tw-ml-0.5">*</span>
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <FormInput
                type="email"
                placeholder="Email"
                error={errors.email?.message}
                {...register('email', {
                  required: 'Email must be required.',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Email is not valid.'
                  }
                })}
              />
            </div>
          </div>

          {/* Title */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Title<span className="tw-text-red-500 tw-ml-0.5">*</span>
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <FormInput
                type="text"
                placeholder="Title"
                error={errors.title?.message}
                {...register('title', { required: 'Title must be required.' })}
              />
            </div>
          </div>

          {/* Terminal Date */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Terminal date<span className="tw-text-red-500 tw-ml-0.5">*</span>
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <Controller
                control={control}
                name="terminal_date"
                rules={{ required: 'Terminal date must be required.' }}
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={(date) => field.onChange(adjustToNoon(date))}
                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-bg-white tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                    placeholderText="DD-MM-YYYY"
                    dateFormat="dd-MM-yyyy"
                    fixedHeight
                    isClearable={true}
                  />
                )}
              />
              {errors.terminal_date && (
                <p className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-1">
                  {errors.terminal_date.message}
                </p>
              )}
            </div>
          </div>

          {/* Site Office */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Site office<span className="tw-text-blue-500 tw-ml-0.5">*</span>
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <Controller
                control={control}
                name="site_id"
                rules={{ required: { value: true, message: 'Site must be required' } }}
                render={({ field }) => (
                  <Select
                    closeMenuOnSelect={true}
                    options={siteOptions}
                    value={siteOptions?.find((opt) => opt.value === field.value) || defaultSiteOption}
                    placeholder="Select..."
                    classNamePrefix="react-select"
                    onChange={handleSiteChange}
                  />
                )}
              />
              {errors.site_id && (
                <p className="tw-text-xs tw-text-red-600 tw-font-medium tw-mt-1">
                  {errors.site_id.message}
                </p>
              )}
            </div>
          </div>

          {/* Department */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Department
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <Controller
                control={control}
                name="department_id"
                render={({ field }) => (
                  <Select
                    isClearable
                    options={departmentOptions}
                    value={departmentOptions && departmentOptions.find((opt) => opt.value === field.value)}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption?.value);
                    }}
                    placeholder={loadingDepartments ? 'Loading departments...' : 'Select...'}
                    classNamePrefix="react-select"
                    className="tw-w-full"
                    isLoading={loadingDepartments}
                    isDisabled={loadingDepartments}
                  />
                )}
              />
            </div>
          </div>

          {/* Remark */}
          <div className="tw-grid tw-grid-cols-12 tw-items-start tw-gap-3">
            <label className="tw-col-span-12 sm:tw-col-span-3 sm:tw-text-right tw-text-sm tw-font-medium tw-text-slate-700 sm:tw-pt-2">
              Remark
            </label>
            <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
              <FormInput
                type="textarea"
                rows={10}
                placeholder="Note in special cases, keep the email for a period of time."
                {...register('remark')}
              />
            </div>
          </div>
        </div>

        {/* Card Footer */}
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-px-5 tw-py-4 tw-border-t tw-border-slate-100 tw-bg-slate-50 tw-rounded-b-2xl">
          <Button type="submit" variant="primary" size="md">
            <FaRegSave />
            Submit
          </Button>
          <Button type="button" variant="danger" size="md" onClick={handleCancel}>
            <FaBan />
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

RequestTerminalAccountForm.propTypes = {};

export default RequestTerminalAccountForm;
